"use client";

import React from "react";
import {
  Box,
  Chip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";

const typeLabelMap = {
  script: "Скрипт",
  objection: "Заперечення",
  faq: "FAQ",
  note: "Нотатка",
};

const typeColorMap = {
  script: "primary",
  objection: "error",
  faq: "info",
  note: "default",
};

const typeStyleMap = {
  script: {
    backgroundColor: "rgba(96, 93, 255, 0.08)",
    borderColor: "rgba(96, 93, 255, 0.25)",
  },
  objection: {
    backgroundColor: "rgba(244, 67, 54, 0.08)",
    borderColor: "rgba(244, 67, 54, 0.25)",
  },
  faq: {
    backgroundColor: "rgba(2, 136, 209, 0.08)",
    borderColor: "rgba(2, 136, 209, 0.25)",
  },
  note: {
    backgroundColor: "rgba(0, 0, 0, 0.03)",
    borderColor: "rgba(0, 0, 0, 0.08)",
  },
};

const renderMarks = (text) => {
  const tokenRegex =
    /(\*\*[^*]+\*\*|__[^_]+__|~~[^~]+~~|\*[^*]+\*|\[[^\]]+\]\([^)]+\))/g;
  return text
    .split(tokenRegex)
    .filter(Boolean)
    .map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={`b-${index}`}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith("__") && part.endsWith("__")) {
        return <u key={`u-${index}`}>{part.slice(2, -2)}</u>;
      }
      if (part.startsWith("~~") && part.endsWith("~~")) {
        return <s key={`s-${index}`}>{part.slice(2, -2)}</s>;
      }
      if (part.startsWith("*") && part.endsWith("*")) {
        return <em key={`i-${index}`}>{part.slice(1, -1)}</em>;
      }
      if (part.startsWith("[") && part.includes("](") && part.endsWith(")")) {
        const match = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (match) {
          return (
            <a
              key={`l-${index}`}
              href={match[2]}
              target="_blank"
              rel="noopener noreferrer"
            >
              {match[1]}
            </a>
          );
        }
      }
      return <React.Fragment key={`t-${index}`}>{part}</React.Fragment>;
    });
};

const normalizeFontSize = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return null;
  if (/^\d+(\.\d+)?$/.test(trimmed)) return `${trimmed}px`;
  return trimmed;
};

const renderInline = (text) => {
  const tagRegex = /\[(color|size)=([^\]]+)\]([\s\S]*?)\[\/\1\]/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  while ((match = tagRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", value: text.slice(lastIndex, match.index) });
    }
    parts.push({
      type: match[1],
      value: match[3],
      attr: match[2],
    });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push({ type: "text", value: text.slice(lastIndex) });
  }

  return parts.map((part, index) => {
    if (part.type === "color") {
      return (
        <span key={`c-${index}`} style={{ color: part.attr }}>
          {renderInline(part.value)}
        </span>
      );
    }
    if (part.type === "size") {
      const fontSize = normalizeFontSize(part.attr);
      return (
        <span key={`s-${index}`} style={fontSize ? { fontSize } : undefined}>
          {renderInline(part.value)}
        </span>
      );
    }
    return (
      <React.Fragment key={`t-${index}`}>
        {renderMarks(part.value)}
      </React.Fragment>
    );
  });
};

const renderBody = (body) => {
  if (!body) return "Текст відсутній";
  const lines = body.split("\n");
  return lines.map((line, index) => (
    <React.Fragment key={`line-${index}`}>
      {renderInline(line)}
      {index < lines.length - 1 && <br />}
    </React.Fragment>
  ));
};

const ScriptTemplateViewer = ({ version, sections, variant = "side" }) => {
  const sourceSections = sections || version?.sections || [];
  if (!sourceSections.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Немає скрипта
      </Typography>
    );
  }

  const rootSx =
    variant === "side"
      ? {
          mt: 0,
          width: "100%",
          border: "1px solid rgba(0,0,0,0.08)",
          borderRadius: 1.5,
          overflow: "hidden",
          minWidth: 320,
        }
      : { mt: 2 };

  return (
    <Box sx={rootSx}>
      {sourceSections.map((section, index) => (
        <Accordion
          key={section.id}
          defaultExpanded={!section.is_collapsed_by_default}
          disableGutters
          square
          sx={{
            borderBottom:
              index === sourceSections.length - 1
                ? "none"
                : "1px solid rgba(0,0,0,0.08)",
            boxShadow: "none",
            "&::before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.8rem" }} />}
            sx={{
              px: 2,
              minHeight: 48,
              "& .MuiAccordionSummary-content": {
                margin: 0,
              },
            }}
          >
            <Typography fontWeight={600}>{section.title}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2, pb: 2 }}>
            {section.items?.length ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {section.items.map((item) => {
                  const label =
                    typeLabelMap[item.item_type] || item.item_type || "Item";
                  const chipColor =
                    typeColorMap[item.item_type] || "default";
                  const typeStyle = typeStyleMap[item.item_type] || {
                    backgroundColor: "transparent",
                    borderColor: "transparent",
                  };
                  return (
                    <Box
                      key={item.id}
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: typeStyle.borderColor,
                        backgroundColor: typeStyle.backgroundColor,
                      }}
                    >
                      <Chip size="small" label={label} color={chipColor} />
                      <Box>
                        <Typography fontWeight={600}>{item.title}</Typography>
                        <Typography variant="body2">
                          {renderBody(item.body)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Немає елементів
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default ScriptTemplateViewer;
